import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { missionDetailResponseSchema, missionResponseSchema, type MissionAssignment, type MissionStatus } from '@unit/shared';
import { useApi } from '../../lib/useApi.js';
import { apiPatch } from '../../lib/api.js';
import { useAction } from '../../lib/useAction.js';
import { ApiError } from '../../components/ApiError.js';
import { ActionError } from '../../components/ActionError.js';
import { formatDate } from '../issuance/FormsPage.js';
import { StatusPill } from '../gear/GearCard.js';
import { MissionStatusPill } from './MissionsPage.js';

/**
 * One mission (REQUIREMENTS §2.6 "Mission view"): what was drawn, who holds
 * it, what is back, what is late. Late rows come first, from the server.
 * Closing the mission is the only edit here; it tells whoever still holds
 * gear for it.
 */
export function MissionDetailPage() {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const state = useApi(id ? `/missions/${id}` : null, missionDetailResponseSchema);
  const setStatus = useAction((status: MissionStatus) => apiPatch(`/missions/${id}`, missionResponseSchema, { status }));

  if (state.kind === 'loading') return <p className="text-slate-500">{t('home.loading')}</p>;
  if (state.kind === 'error') return <ApiError code={state.code} requestId={state.requestId} />;
  // The server says whether this caller may edit *this* mission (story 38).
  const canManage = state.data.canManage;

  const { mission, assignments, soldiers } = state.data;
  const soldier = (uid: string) => soldiers.find((s) => s.uid === uid);
  const out = assignments.filter((a) => !['returned', 'expended', 'cancelled', 'written_off', 'transferred'].includes(a.status));
  const back = assignments.filter((a) => a.status === 'returned' || a.status === 'expended');
  const late = assignments.filter((a) => a.overdue);

  const Row = ({ a }: { a: MissionAssignment }) => {
    const s = soldier(a.soldierUid);
    return (
      <li className="flex flex-wrap items-start justify-between gap-2 py-2" data-testid="mission-line" data-id={a.id} data-overdue={a.overdue}>
        <div>
          <p className="font-medium text-slate-900">
            {i18n.language.startsWith('en') ? a.nameEn : a.nameHe}
            {a.serialNumber ? (
              <span className="latin-inline ms-2 rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-700">{a.serialNumber}</span>
            ) : (
              <span className="ms-2 text-sm text-slate-500">× <span className="latin-inline">{a.quantity}</span></span>
            )}
          </p>
          <p className="text-sm text-slate-600">
            {a.teamId ? (
              <Link to={`/org/${a.teamId ?? a.platoonId}/soldier/${a.soldierUid}`} className="underline-offset-4 hover:underline">{s?.name ?? a.soldierUid}</Link>
            ) : (
              s?.name ?? a.soldierUid
            )}
            {s?.unitName && <span className="text-slate-400"> · {s.unitName}</span>}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1 text-xs text-slate-500">
          <StatusPill status={a.status} />
          {a.overdue && <span className="font-semibold text-red-700" data-testid="mission-line-late">{t('missions.late')}</span>}
          {a.dueDate && <span className="latin-inline">{formatDate(a.dueDate, i18n.language)}</span>}
        </div>
      </li>
    );
  };

  return (
    <section className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200" data-testid="mission-detail">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">{mission.name}</h2>
          {mission.description && <p className="text-sm text-slate-600">{mission.description}</p>}
          <p className="mt-1 text-sm text-slate-500">
            <span className="latin-inline">{formatDate(mission.startAt, i18n.language)}</span>
            {mission.endAt && <> – <span className="latin-inline">{formatDate(mission.endAt, i18n.language)}</span></>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <MissionStatusPill status={mission.status} />
          {canManage && mission.status !== 'closed' && (
            <button
              type="button"
              onClick={() => void setStatus.run(mission.status === 'planned' ? 'active' : 'closed').then((r) => r && state.reload())}
              disabled={setStatus.busy}
              className="rounded-lg px-3 py-1.5 text-sm text-slate-700 ring-1 ring-slate-300 hover:bg-slate-100 disabled:opacity-40"
              data-testid={mission.status === 'planned' ? 'mission-activate' : 'mission-close'}
            >
              {mission.status === 'planned' ? t('missions.activate') : t('missions.close')}
            </button>
          )}
        </div>
      </div>
      <ActionError error={setStatus.error} />

      <p className="mt-3 text-sm text-slate-600" data-testid="mission-summary">
        {t('missions.summary', { out: out.length, back: back.length, late: late.length })}
      </p>
      <Link to="/missions" className="mt-1 inline-block text-sm text-slate-500 underline-offset-4 hover:underline">
        {t('missions.backToList')}
      </Link>

      {late.length > 0 && (
        <>
          <h3 className="mt-4 font-semibold text-red-800">{t('missions.lateSection')}</h3>
          <ul className="divide-y divide-slate-100" data-testid="mission-late">
            {late.map((a) => <Row key={a.id} a={a} />)}
          </ul>
        </>
      )}
      <h3 className="mt-4 font-semibold text-slate-900">{t('missions.outSection')}</h3>
      {out.filter((a) => !a.overdue).length === 0 && <p className="text-sm text-slate-500">{t('missions.nothingOut')}</p>}
      <ul className="divide-y divide-slate-100" data-testid="mission-out">
        {out.filter((a) => !a.overdue).map((a) => <Row key={a.id} a={a} />)}
      </ul>
      <h3 className="mt-4 font-semibold text-slate-900">{t('missions.backSection')}</h3>
      {back.length === 0 && <p className="text-sm text-slate-500">{t('missions.nothingBack')}</p>}
      <ul className="divide-y divide-slate-100" data-testid="mission-back">
        {back.map((a) => <Row key={a.id} a={a} />)}
      </ul>
    </section>
  );
}
